import { withErrorBoundary } from "@sentry/nextjs";
import { FC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { GeneralRoutingLinkButton } from "components/Molecules/GeneralRoutingLinkButton";
import { getImageFormat } from "lib/utils";
import { Carousel2Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Carousel2Content["carousel2Options"][0];
}

const Card: FC<Props> = withErrorBoundary(
  ({
    className = "",
    content: {
      guidelineLogo,
      description,
      buttonLink,
      buttonLabel,
      partnerLogos: { data: partnerLogos },
    },
  }) => {
    const logoTop = guidelineLogo.data?.guidelineLogo;

    return (
      <StyledCard className={className}>
        <div className="Card__innerContainer">
          <div className="Card__mainContent">
            {logoTop && (
              <LegacyImage
                {...logoTop}
                className="Card__mainContentLogo"
                transform={{ format: getImageFormat(guidelineLogo.data.guidelineLogo.ext) }}
              />
            )}
            <Markdown className="Card__text Font_24_32_450">{description}</Markdown>
            {buttonLink && (
              <GeneralRoutingLinkButton className="Card__mainContentButton" secondary href={buttonLink}>
                {buttonLabel}
              </GeneralRoutingLinkButton>
            )}
          </div>
          {!!partnerLogos.length && (
            <div className="Card__bottomLogos">
              {partnerLogos.map(({ partnerLogo, id }) => (
                <LegacyImage
                  {...partnerLogo}
                  transform={{ format: getImageFormat(partnerLogo.ext) }}
                  className="Card__bottomLogo"
                  key={id}
                />
              ))}
            </div>
          )}
        </div>
      </StyledCard>
    );
  },
  {}
);

const StyledCard = styled.li`
  padding: 40px;
  background: url("/images/noise.png"),
    radial-gradient(49.55% 37.88% at 74.19% 51.44%, rgb(85 50 250 / 20%) 17.04%, rgb(205 216 220 / 0%) 98.18%),
    radial-gradient(85.93% 39.2% at 91.09% 52.37%, #ffcd85 0%, rgb(252 207 143 / 0%) 100%),
    radial-gradient(99.76% 40.23% at 36.62% 90.65%, #5532fa 6.99%, #efa085 66.87%, #fcdac4 100%);
  background-blend-mode: overlay, normal, normal;
  background-repeat: no-repeat;

  @media (min-width: 1200px) {
    background-size: 100% 100%;
  }

  .Card__innerContainer {
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.fog["00"]};
    overflow: hidden;
  }

  .Card__mainContent {
    padding: 48px;
  }

  .Card__mainContentLogo {
    position: relative;
    margin-bottom: 32px;
  }

  .Card__text {
    p:not(:first-child) {
      margin-top: 20px;
    }
  }

  .Card__mainContentButton {
    margin-top: 32px;
    width: fit-content;
  }

  .Card__bottomLogos {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 40px 48px;
    background: ${({ theme }) => theme.colors.fog["10"]};
  }

  .Card__bottomLogo:not(:first-child) {
    margin-left: 24px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Card__mainContent {
      padding: 32px;
    }

    .Card__bottomLogos {
      padding: 32px;
    }
  }
`;

export { Card };
